import { mod } from '@scripts/helpers/utils';

/**
 * Handles Home and End key navigation for a list.
 * @param {Object} options
 * @param {KeyboardEvent} options.event - The keyboard event.
 * @param {Array<Element>} options.items - The list of item elements.
 * @param {Function} options.getTrigger - Function to get the focusable element from an item.
 * @returns {boolean} True if a key was handled, false otherwise.
 */
export function handleHomeEndNavigation({ event, items, getTrigger }) {
	if (!(event instanceof KeyboardEvent)) return false;
	if (!items.length) return false;

	const key = event.key.toLowerCase();

	if (key === 'home') {
		event.preventDefault();

		getTrigger(items[0])?.focus();

		return true;
	}

	if (key === 'end') {
		event.preventDefault();

		getTrigger(items[items.length - 1])?.focus();

		return true;
	}

	return false;
}

/**
 * Handles arrow key navigation for a list.
 * @param {Object} options
 * @param {KeyboardEvent} options.event
 * @param {Array<Element>} options.items
 * @param {Function} options.getTrigger - Returns the focusable element from an item.
 * @param {Function} [options.getItem] - Returns the item element from a focused element.
 * @param {boolean} [options.loop=true] - Cycle at beginning/end.
 * @param {string} options.prevKey - Key name for previous item (e.g. 'arrowup', 'arrowleft').
 * @param {string} options.nextKey - Key name for next item (e.g. 'arrowdown', 'arrowright').
 * @returns {boolean} True if a key was handled, false otherwise.
 */
function arrowKeyNavigationHandler({
	event,
	items,
	getTrigger,
	getItem = (el) => el,
	loop = true,
	prevKey,
	nextKey
}) {
	if (!(event instanceof KeyboardEvent)) return false;

	const key = event.key.toLowerCase();
	const itemArray = Array.from(items);

	if (!itemArray.length) return false;

	if (handleHomeEndNavigation({ event, items: itemArray, getTrigger })) return true;

	if (key !== prevKey && key !== nextKey) return false;

	const focusedElement = document.activeElement;
	const focusedItem = getItem(focusedElement);
	const index = itemArray.indexOf(focusedItem);

	if (index === -1) return false;

	event.preventDefault();

	if (key === prevKey) {
		if (loop) {
			getTrigger(itemArray[mod(index - 1, itemArray.length)])?.focus();
		} else if (index > 0) {
			getTrigger(itemArray[index - 1])?.focus();
		}
	} else {
		if (loop) {
			getTrigger(itemArray[mod(index + 1, itemArray.length)])?.focus();
		} else if (index < itemArray.length - 1) {
			getTrigger(itemArray[index + 1])?.focus();
		}
	}

	return true;
}

/**
 * Handles vertical list keyboard navigation (ArrowUp/ArrowDown).
 * @param {Object} options
 * @param {KeyboardEvent} options.event
 * @param {Array<Element>} options.items
 * @param {Function} options.getTrigger - Returns the focusable element from an item.
 * @param {Function} [options.getItem] - Returns the item element from a focused element.
 * @param {boolean} [options.loop=true] - Cycle at beginning/end.
 * @returns {boolean} True if a key was handled, false otherwise.
 */
export function verticalKeyNavigationHandler(options) {
	return arrowKeyNavigationHandler({ ...options, prevKey: 'arrowup', nextKey: 'arrowdown' });
}

/**
 * Handles horizontal list keyboard navigation (ArrowLeft/ArrowRight).
 * @param {Object} options
 * @param {KeyboardEvent} options.event
 * @param {Array<Element>} options.items
 * @param {Function} options.getTrigger - Returns the focusable element from an item.
 * @param {Function} [options.getItem] - Returns the item element from a focused element.
 * @param {boolean} [options.loop=true] - Cycle at beginning/end.
 * @returns {boolean} True if a key was handled, false otherwise.
 */
export function horizontalKeyNavigationHandler(options) {
	return arrowKeyNavigationHandler({ ...options, prevKey: 'arrowleft', nextKey: 'arrowright' });
}
